import {Component, Inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent, MatDialogRef,
  MatDialogTitle
} from "@angular/material/dialog";
import {MatIcon} from "@angular/material/icon";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatFormField} from "@angular/material/input";
import {MatOption, MatSelect} from "@angular/material/select";
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {PipelinesService} from "../../pipelines.service";
import Group from "../../../interfaces/group";
import {User} from "../../../interfaces/user";
import {MatCheckbox, MatCheckboxChange} from "@angular/material/checkbox";
import {Person} from "../../../interfaces/person";
import {MatTooltip} from "@angular/material/tooltip";
import UserPipelinePerm from "../../../interfaces/user-pipeline-permission";
import {BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, Observable, startWith} from "rxjs";
import {map} from "rxjs/operators";
import Permission from "../../../interfaces/permission";

export enum ShareOwnerType {GROUP, USER}

@Component({
  selector: 'app-pipelines-share-modal',
  standalone: false,
  templateUrl: './pipelines-share-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './pipelines-share-modal.component.scss',
})
export class PipelinesShareModalComponent implements OnInit{
  public groups: Group[] = []
  public users: Person[]  = []
  public selectedShareType: ShareOwnerType = ShareOwnerType.GROUP
  public selectedOwnerType: ShareOwnerType = ShareOwnerType.GROUP
  public owner: Group | Person

  public viewChangeOwnerPanel: boolean = false;
  public searchControl: FormControl = new FormControl()

  public shareGroups: {name: string, id: string, shareType:string, _edit: boolean, _delete:boolean, permission_id: string | null}[] = []
  private _userIds:string[]
  public filteredOptions: Observable<any[]>;
  private _dataLoaded$ = new BehaviorSubject(false)
  private _me: Person
  constructor(@Inject(MAT_DIALOG_DATA) public data: any, private _pipelineService: PipelinesService, private dialogRef:MatDialogRef<PipelinesShareModalComponent>) {
  }

  ngOnInit() {
    this.owner = this.data.pipeline.person !== null ? this.data.pipeline.person : this.data.pipeline.relatedGroup

    if(this.data.pipeline.group_id==null && this.data.pipeline.person_id!==null){
      this.selectedShareType = ShareOwnerType.USER
      this.selectedOwnerType = ShareOwnerType.USER
    }

    this._formatGroupUserMembers(this.data.pipeline.acls)

    this._pipelineService.getPermissionGroups().subscribe((result:any) => {
      this.groups = [...result.getGroups.filter((group:Group) => group.groupType?.name.toLowerCase() == 'department')]
      this.users = [...result.get_active_users]
      this.users = this.users.sort((a:Person, b:Person) => a.name.localeCompare(b.name))
      this.groups = this.groups.sort((a:Group, b:Group) => a.name.localeCompare(b.name))
      this._dataLoaded$.next(true)
    })

    this._pipelineService.currentUserGroupIds$.subscribe((result) => this._userIds = result)
    this._pipelineService.currentUser$.subscribe((result) => this._me = result.user_profile.user.person)

    this.filteredOptions = combineLatest([
      this.searchControl.valueChanges.pipe(
        startWith(''),
        debounceTime(150),
        distinctUntilChanged()
    ),
      this._dataLoaded$
    ]).pipe(
      map(([value]: [string, boolean]) =>{
        return this._filter(value || '')
      } )
    )
  }

  public viewGroupMembers(group : Person | Group){
  }

  private _filter(value: string | Group | Person) :any[] {

    const filterValue = (typeof value === 'string' ? value : value?.name ?? '').toLowerCase();

    const source: (Group | Person) [] =
      this.selectedShareType === ShareOwnerType.GROUP
        ? this.groups
        : this.users;

    return source.filter(option =>
      option.name.toLowerCase().includes(filterValue)
    );

  }

  private _formatGroupUserMembers(rows:any){
    rows.forEach((acl:any) => {
      let member: Group | Person = acl.acc.group || acl.acc.user.person

      let permission: Permission = acl.permissions[0]
      if(permission){
        let permissionForPipeline:any = {
          name: member?.name,
          id: member?.id,
          shareType: member?.__typename==='Group' ? 'Group' : 'Person',
          _edit: permission ? permission?._edit : false,
          _delete: permission ? permission?._delete : false,
          permission_id: permission?.id
        }
        this.shareGroups.push(permissionForPipeline)
      }

    })
  }

  public onOwnerChange(event:any) {
    this.owner = event.value
  }

  public isOwner(){
    return this._me.id === this.owner.id
  }

  public changeShareType(event:any){
    this.selectedShareType = event.value;
    this.searchControl.reset()
  }

  displayFn(value:any){
    return value?.name || value
  }

  public onSelectShare(event:any){
    if(event.option.value!==null){

      let permissionForPipeline:{name: string, id: string, shareType:string, _edit: boolean, _delete:boolean, permission_id: string | null} = {
        name: event.option.value.name,
        id: event.option.value.id,
        shareType: event.option.value.__typename==='Group' ? 'Group' : 'Person',
        _edit: false,
        _delete: false,
        permission_id: null
      }
      this.shareGroups.push(permissionForPipeline)
    }
  }

  public removeShareGroupFromList(group: any){
    if(group.permission_id){
      this._pipelineService.deletePipelinePermission(group.permission_id).subscribe({
        next:() => this.shareGroups = this.shareGroups.filter((gp: {name: string, id: string, _edit: boolean, _delete:boolean}) => gp.id !== group.id)
      })
    } else {
      this.shareGroups = this.shareGroups.filter((gp: {name: string, id: string, _edit: boolean, _delete:boolean}) => gp.id !== group.id)
    }
  }

  public onEditPermissionChange(event:MatCheckboxChange, group:any){
    group._edit = event.checked
  }

  public onDeletePermissionChange(event:MatCheckboxChange, group:any){
    group._delete = event.checked
  }

  public changeOwner(){
    this.viewChangeOwnerPanel = !this.viewChangeOwnerPanel
  }

  public sharePipeline(){
    let formattedGroups:any[] = []
    this.shareGroups.forEach((group: any) => {
      let flatGroup = {
        group_id: group.shareType == 'Group' ? group.id : null,
        user_id: group.shareType == 'Person' ? group.id : null,
        _edit: group._edit,
        _delete: group._delete
      }
      formattedGroups.push(flatGroup)
    })
    let groupId = this.owner.__typename=='Group' ? this.owner.id : null
    let personId = this.owner.__typename=='Group' ? null : this.owner.id
    this._pipelineService.sharePipelineFilter(this.data.pipeline.id, formattedGroups,groupId ?? null, personId ?? null).subscribe((response) => {
      this._pipelineService.getPipelineViews()
      this.dialogRef.close()
    })
  }

  protected readonly ShareOwnerType = ShareOwnerType;
}
